import { SearchRequest } from '@mango-engineering/maknet-client-sdk/lib/shop/search/types';
import { config } from '../../config/env';
import ProductService from '../../services/product/product.service';
import {
  BrandAndCatrgoryRecommendationResult,
  HomeRecommendationRequest,
  HomeRecommendationResult,
  RecommendationType,
  RecommendationPieceRequest,
  RecommendationPayload,
} from '../../types/common';
import { AuthErrorType } from '../../types/session.types';
import { get, post } from '../../utils/api.util';
import {
  AppMode,
  FRESH_FOOD_FILTER,
  isStorePickupRecommendationFlagEnabled,
  PRODUCT_PAGE_SIZE,
  URL_CONSTANTS,
} from '../../utils/constants';

export class RecommendationsServices {
  public getBrandAndCategoryRecommendationProducts = async (
    payload: RecommendationPieceRequest,
    authKey?: string,
  ): Promise<BrandAndCatrgoryRecommendationResult> => {
    let url = `${config.SEARCH_API_ENDPOINT_BASE}${URL_CONSTANTS.SEARCH_BASED}`;
    if (payload.page) {
      url = `${url}/${payload.type}`;
    }
    const timeoutPromise = new Promise((_, reject) =>
      setTimeout(() => reject(new Error(AuthErrorType.INTERNAL_TECHNICAL_ERROR)), 3000),
    );
    try {
      const data: BrandAndCatrgoryRecommendationResult = await Promise.race([
        post(url, payload, authKey),
        timeoutPromise,
      ]);
      if (!data) {
        throw new Error(AuthErrorType.INTERNAL_TECHNICAL_ERROR);
      }
      return data;
    } catch (err: unknown) {
      console.info('Error in getBrandAndCategoryRecommendationProducts', err);
      return {
        recommendations: [],
        total: 0,
        personalised: false,
      };
    }
  };

  private buildHomeRecommendationFilter(
    storeCodes?: string[],
    recommendationType?: RecommendationType,
    appMode?: AppMode,
    isStoreCodesForRecommendationEnabled?: boolean,
  ): string {
    let baseFilter = '';

    if (recommendationType === RecommendationType.FRESHFOOD) {
      baseFilter = FRESH_FOOD_FILTER;
    } else {
      if (appMode === AppMode.express) {
        baseFilter = `expressDelivery:[${storeCodes}]`;
      } else if (appMode === AppMode.storePickup && isStorePickupRecommendationFlagEnabled) {
        baseFilter = `storePickupStores:[${storeCodes}]`;
      }
    }
    if (isStoreCodesForRecommendationEnabled && storeCodes?.length) {
      const combined = baseFilter ? `storeCodes:[${storeCodes}]&&${baseFilter}` : `storeCodes:[${storeCodes}]`;

      return encodeURIComponent(combined);
    }

    return baseFilter ? encodeURIComponent(baseFilter) : '';
  }

  public getHomeRecommendationProducts = async (
    payload: HomeRecommendationRequest,
    authKey: string,
    recommendationVariant: string,
    userIdOrAnonymousId: number | string,
    storeCodes?: string[],
    recommendationType?: RecommendationType,
    appMode?: AppMode,
    isStoreCodesForRecommendationEnabled?: boolean,
  ): Promise<HomeRecommendationResult> => {
    const timeoutPromise = new Promise((_, reject) =>
      setTimeout(() => reject(new Error(AuthErrorType.INTERNAL_TECHNICAL_ERROR)), 3000),
    );
    const baseUrl = `${config.RECOMMENDATION_API_ENDPOINT}${URL_CONSTANTS.USER_BASED}${recommendationVariant}/${userIdOrAnonymousId}`;

    const filter = this.buildHomeRecommendationFilter(
      storeCodes,
      recommendationType,
      appMode,
      isStoreCodesForRecommendationEnabled,
    );
    try {
      const data: HomeRecommendationResult = await Promise.race([
        get(baseUrl, authKey, { ...payload, ...(filter && { filters: filter }) }),
        timeoutPromise,
      ]);
      if (!data) {
        throw new Error(AuthErrorType.INTERNAL_TECHNICAL_ERROR);
      }
      return data;
    } catch (err: unknown) {
      console.info('Error in getHomeRecommendationProducts', err);
      return {
        products: [],
        algorithm: '',
        recommendationType: '',
        totalCount: 0,
        actualCount: 0,
        personalised: false,
      };
    }
  };

  public getFrequentlyBoughtTogetherRecommendationProduct = async (
    payload: RecommendationPayload,
  ): Promise<HomeRecommendationResult> => {
    const encodedFilter = encodeURIComponent(
      `expressDelivery:[${payload?.recommendationStoreCodes ?? payload?.storeCodes}]`,
    );
    const baseUrl = `${config.RECOMMENDATION_API_ENDPOINT}${URL_CONSTANTS.PRODUCT_BASED}${payload.recommendationVariant}/${payload?.productId}`;
    const parameter = payload?.page ? { page: payload?.page, size: PRODUCT_PAGE_SIZE } : undefined;
    const timeoutPromise = new Promise((_, reject) =>
      setTimeout(() => reject(new Error(AuthErrorType.INTERNAL_TECHNICAL_ERROR)), 3000),
    );
    const url = payload?.isExpressMode ? baseUrl + `?filters=${encodedFilter}` : baseUrl;
    try {
      const data: HomeRecommendationResult = await Promise.race([
        get(url, payload?.accessToken ?? '', parameter),
        timeoutPromise,
      ]);
      if (!data) {
        throw new Error(AuthErrorType.INTERNAL_TECHNICAL_ERROR);
      }
      return data;
    } catch (err: unknown) {
      console.info('Error in getFrequentlyBoughtTogetherRecommendationProduct', err);
      return {
        products: [],
        algorithm: '',
        recommendationType: '',
        totalCount: 0,
        actualCount: 0,
        personalised: false,
      };
    }
  };

  public getSearchProducts = async (
    accessToken: string,
    payload?: SearchRequest,
    allowAlcohol?: boolean,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  ): Promise<any | null> => {
    try {
      const result = await ProductService.search(payload!, accessToken, allowAlcohol);
      return result;
    } catch (err: unknown) {
      console.info('Error in getSearchProducts', err);
      return null;
    }
  };
}

const RecommendationServices = new RecommendationsServices();

export default RecommendationServices;
